import { useId, useState, useEffect, type ComponentProps } from 'react';
import { FieldInfo, useFieldContext } from './shared';
import { Label } from '../label';
import { Input } from '../input';
import { Tooltip, TooltipTrigger, TooltipContent } from '../tooltip';
import { UploadIcon, ExternalLinkIcon, CropIcon } from 'lucide-react';
import { ImageEditor } from './-components/image-editor';
import { toast } from 'sonner';

export default function ImageField({
	label,
	url,
	className,
	aspectRatio,
	enableCrop = false,
	...props
}: ComponentProps<'input'> & {
	label: string;
	url?: string;
	aspectRatio?: { width: number; height: number };
	enableCrop?: boolean;
}) {
	const field = useFieldContext<File | undefined>();
	const id = useId();
	const fieldId = `field-${field.name}-${id}-file`;
	const [isEditing, setIsEditing] = useState(false);
	const [pendingFile, setPendingFile] = useState<File | null>(null);
	const [originalFile, setOriginalFile] = useState<File | null>(null); // Track original uncropped file
	const [cropArea, setCropArea] = useState<{ x: number; y: number; width: number; height: number } | null>(null); // Track crop area

	// Reset crop area and original file when field value is cleared or changes to undefined/string (URL)
	useEffect(() => {
		if (!field.state.value || typeof field.state.value === 'string') {
			setCropArea(null);
			setOriginalFile(null);

			// Clear the file input element
			const input = document.getElementById(field.name) as HTMLInputElement | null;
			if (input) {
				input.value = '';
			}

			// Clear the image preview
			const image = document.getElementById(fieldId) as HTMLImageElement | null;
			if (image) {
				image.src = url || '';
			}
		}
	}, [field.state.value, field.name, fieldId, url]);

	return (
		<div className={className}>
			<div className="text-muted-foreground dark:bg-input/30 dark:hover:bg-input/50 border-input relative rounded-md border bg-transparent shadow-xs transition-colors">
				{label && (
					<Label
						data-slot="form-label"
						data-error={field.state.meta.isTouched && !field.state.meta.isValid}
						className="data-[error=true]:text-destructive floating pointer-events-none w-full truncate px-3.5 py-[17px]"
						htmlFor={field.name}
					>
						{label}
					</Label>
				)}
				<Input
					type="file"
					id={field.name}
					name={field.name}
					ref={(ref) => {
						if (!ref) return;
						if (!field.state.value) return;
						if (!(field.state.value instanceof File)) return;

						// Create FileList so we can restore persisted file on input
						const dataTransfer = new DataTransfer();
						dataTransfer.items.add(field.state.value);
						ref.files = dataTransfer.files;

						// Restore image preview
						const image = document.getElementById(fieldId) as HTMLImageElement;
						image.src = URL.createObjectURL(field.state.value);
					}}
					onChange={async (event) => {
						const file = event.target.files?.[0];
						if (!file) return;

						// Clear all previous state when uploading a new image
						setCropArea(null);
						setOriginalFile(null);
						setPendingFile(null);

						// Validate image dimensions if aspectRatio is provided
						if (aspectRatio) {
							const img = new Image();
							const url = URL.createObjectURL(file);

							const handleLoad = () => {
								URL.revokeObjectURL(url);

								// Check if image is too small
								if (img.width < aspectRatio.width || img.height < aspectRatio.height) {
									const errorMsg = `Image must be at least ${aspectRatio.width}x${aspectRatio.height} pixels. Uploaded image is ${img.width}x${img.height} pixels.`;

									field.setMeta((prev) => ({
										...prev,
										errors: [errorMsg],
										isTouched: true,
									}));

									toast.error('Image too small', {
										description: errorMsg,
									});

									// Clear the file input
									event.target.value = '';
									return;
								}

								// Image is valid
								if (enableCrop) {
									// Store as original and open editor
									setOriginalFile(file);
									setPendingFile(file);
									setIsEditing(true);
								} else {
									// Just set the file without opening editor
									field.handleChange(file);
									const image = document.getElementById(fieldId) as HTMLImageElement;
									if (image) {
										image.src = URL.createObjectURL(file);
									}
								}
							};

							const handleError = () => {
								URL.revokeObjectURL(url);
								const errorMsg = 'Failed to load image. Please try a different file.';

								field.setMeta((prev) => ({
									...prev,
									errors: [errorMsg],
									isTouched: true,
								}));

								toast.error('Invalid image', {
									description: errorMsg,
								});

								event.target.value = '';
							};

							img.addEventListener('load', handleLoad);
							img.addEventListener('error', handleError);
							img.src = url;
						} else {
							// No aspect ratio
							if (enableCrop) {
								// Store as original and open editor
								setOriginalFile(file);
								setPendingFile(file);
								setIsEditing(true);
							} else {
								// Just set the file without opening editor
								field.handleChange(file);
								const image = document.getElementById(fieldId) as HTMLImageElement;
								if (image) {
									image.src = URL.createObjectURL(file);
								}
							}
						}
					}}
					className="h-72 w-full cursor-pointer px-3 py-6 opacity-0"
					{...props}
				/>

				<img
					id={fieldId}
					src={url === '' ? undefined : url}
					width="100%"
					height={288}
					className="pointer-events-none absolute top-0 left-0 hidden h-72 w-full rounded-md object-cover"
					onLoad={() => {
						const image = document.getElementById(fieldId) as HTMLImageElement;
						image.style.display = 'block';
					}}
					onError={() => {
						const image = document.getElementById(fieldId) as HTMLImageElement;
						image.style.display = '';
					}}
				/>

				<div className="pointer-events-none absolute top-0 right-0 bottom-0 left-0 flex items-center justify-center">
					<div className="bg-background/50 rounded-lg p-2">
						<UploadIcon className="text-foreground size-6" />
					</div>
				</div>

				{/* Only show external link if we have a URL and haven't changed the image locally */}
				{url && !field.state.value && (
					<Tooltip>
						<TooltipTrigger asChild>
							<a
								href={url}
								download
								target="_blank"
								rel="noopener noreferrer"
								className="bg-background/50 absolute right-2 bottom-2 rounded-lg p-2"
							>
								<ExternalLinkIcon className="text-foreground size-6" />
							</a>
						</TooltipTrigger>
						<TooltipContent>Open image in a new tab</TooltipContent>
					</Tooltip>
				)}

				{enableCrop && field.state.value && originalFile && (
					<Tooltip>
						<TooltipTrigger asChild>
							<button
								type="button"
								onClick={async () => {
									if (!(field.state.value instanceof File)) return;

									// Always use the original uncropped file for editing
									const fileToEdit = originalFile;

									// Validate dimensions if aspectRatio is provided
									if (aspectRatio) {
										const img = new Image();
										const url = URL.createObjectURL(fileToEdit);

										const handleLoad = () => {
											URL.revokeObjectURL(url);

											// Check if image is too small
											if (img.width < aspectRatio.width || img.height < aspectRatio.height) {
												const errorMsg = `Image must be at least ${aspectRatio.width}x${aspectRatio.height} pixels. Current image is ${img.width}x${img.height} pixels.`;

												field.setMeta((prev) => ({
													...prev,
													errors: [errorMsg],
													isTouched: true,
												}));

												toast.error('Image too small', {
													description: errorMsg,
												});

												return;
											}

											// Image is valid, open editor with original file
											setPendingFile(fileToEdit);
											setIsEditing(true);
										};

										const handleError = () => {
											URL.revokeObjectURL(url);
											toast.error('Invalid image', {
												description: 'Failed to load the current image.',
											});
										};

										img.addEventListener('load', handleLoad);
										img.addEventListener('error', handleError);
										img.src = url;
									} else {
										setPendingFile(fileToEdit);
										setIsEditing(true);
									}
								}}
								className="bg-background/50 absolute right-2 bottom-2 rounded-lg p-2"
							>
								<CropIcon className="text-foreground size-6" />
							</button>
						</TooltipTrigger>
						<TooltipContent>Edit image</TooltipContent>
					</Tooltip>
				)}
			</div>
			<FieldInfo fieldState={field.state} />

			{enableCrop && (
				<ImageEditor
					open={isEditing}
					file={pendingFile}
					aspectRatio={aspectRatio}
					initialCropArea={cropArea || undefined}
					onSave={(file, newCropArea) => {
						// Store the original file for re-editing (only if not already stored)
						if (!originalFile) {
							setOriginalFile(pendingFile);
						}

						// Store the crop area for re-editing
						setCropArea(newCropArea);

						// Update the field value - this is what gets submitted to backend
						field.handleChange(file);

						// Update the file input element
						const input = document.getElementById(field.name) as HTMLInputElement;
						if (input) {
							const dataTransfer = new DataTransfer();
							dataTransfer.items.add(file);
							input.files = dataTransfer.files;
						}

						// Preview the cropped image
						const image = document.getElementById(fieldId) as HTMLImageElement;
						if (image) {
							image.src = URL.createObjectURL(file);
						}

						// Close editor and clear pending file after successful save
						setIsEditing(false);
						setPendingFile(null);
					}}
					onOpenChange={(open) => {
						if (!open) {
							// Just close the editor, keep pendingFile for potential reopening
							setIsEditing(false);

							// Only clear everything if there's no saved value
							if (!field.state.value) {
								const input = document.getElementById(field.name) as HTMLInputElement;
								if (input) {
									input.value = '';
								}
								setPendingFile(null);
								setCropArea(null);
								setOriginalFile(null);
							}
						}
					}}
				/>
			)}
		</div>
	);
}
